import Constants from '../assests/Constants/constants';

const FALLBACK_CARDS = Constants.PaymentAcceptanceConstantsAccountcards;

const cardOrFallback = (value, fallback) => value || fallback;

/**
 * Method-side system card for a payment-acceptance line.
 * Receipts (תקבול) debit the acceptance method card;
 * payments (תשלום) credit the payment method card.
 */
export const getPaymentAcceptanceMethodAccountId = (
    itemType,
    systemAccounts = {},
    paymentType = 'תקבול',
) => {
    const isReceipt = paymentType === 'תקבול';
    const generalCheck = cardOrFallback(systemAccounts.generalCheckCardId, FALLBACK_CARDS.GeneralCheck);
    const generalCash = cardOrFallback(systemAccounts.generalCashCardId, FALLBACK_CARDS.GeneralCash);
    const generalCreditCard = cardOrFallback(
        systemAccounts.generalCreditCardCardId,
        FALLBACK_CARDS.GeneralCreditCard,
    );
    const generalBank = cardOrFallback(systemAccounts.generalBankCardId, FALLBACK_CARDS.GeneralBank);

    const cards = {
        acceptanceCheckCardId: cardOrFallback(systemAccounts.acceptanceCheckCardId, generalCheck),
        paymentCheckCardId: cardOrFallback(systemAccounts.paymentCheckCardId, generalCheck),
        acceptanceCashCardId: cardOrFallback(systemAccounts.acceptanceCashCardId, generalCash),
        paymentCashCardId: cardOrFallback(systemAccounts.paymentCashCardId, generalCash),
        acceptanceCreditCardCardId: cardOrFallback(
            systemAccounts.acceptanceCreditCardCardId,
            generalCreditCard,
        ),
        paymentCreditCardCardId: cardOrFallback(
            systemAccounts.paymentCreditCardCardId,
            generalCreditCard,
        ),
        acceptanceBankTransactionCardId: cardOrFallback(
            systemAccounts.acceptanceBankTransactionCardId,
            generalBank,
        ),
        paymentBankTransactionCardId: cardOrFallback(
            systemAccounts.paymentBankTransactionCardId,
            generalBank,
        ),
        generalOffsetCardId: cardOrFallback(systemAccounts.generalOffsetCardId, FALLBACK_CARDS.GeneralOffset),
    };

    switch (itemType) {
        case 'checks':
            return isReceipt ? cards.acceptanceCheckCardId : cards.paymentCheckCardId;
        case 'creditCards':
            return isReceipt ? cards.acceptanceCreditCardCardId : cards.paymentCreditCardCardId;
        case 'cash':
            return isReceipt ? cards.acceptanceCashCardId : cards.paymentCashCardId;
        case 'bankTransactions':
            return isReceipt
                ? cards.acceptanceBankTransactionCardId
                : cards.paymentBankTransactionCardId;
        case 'offsets':
            return cards.generalOffsetCardId;
        default:
            return isReceipt ? cards.acceptanceCheckCardId : cards.paymentCheckCardId;
    }
};

/** CaseSystemAccountSettings field (PascalCase) holding the method card per line type and payment type. */
const METHOD_SETTING_FIELDS = {
    checks: { 'תקבול': 'AcceptanceCheckCardId', 'תשלום': 'PaymentCheckCardId' },
    creditCards: { 'תקבול': 'AcceptanceCreditCardCardId', 'תשלום': 'PaymentCreditCardCardId' },
    cash: { 'תקבול': 'AcceptanceCashCardId', 'תשלום': 'PaymentCashCardId' },
    bankTransactions: { 'תקבול': 'AcceptanceBankTransactionCardId', 'תשלום': 'PaymentBankTransactionCardId' },
    offsets: { 'תקבול': 'GeneralOffsetCardId', 'תשלום': 'GeneralOffsetCardId' },
};

export const getPaymentMethodSettingField = (itemType, paymentType) =>
    METHOD_SETTING_FIELDS[itemType]?.[paymentType] || null;

/** Side of a payment line that holds the method (vault) card: receipts debit it, payments credit it. */
export const getPaymentMethodSide = (paymentType) => (paymentType === 'תקבול' ? 'debit' : 'credit');

/**
 * Debit and credit for a new payment-acceptance line.
 *
 *   תקבול: credit = client, debit = acceptance method card
 *   תשלום: debit = supplier, credit = payment method card (or GeneralOffset for offsets)
 */
export const getPaymentAcceptanceLineAccounts = ({
    paymentType,
    itemType,
    partyAccountId,
    systemAccounts = {},
} = {}) => {
    const party = partyAccountId || '';
    const methodAccountId = getPaymentAcceptanceMethodAccountId(
        itemType,
        systemAccounts,
        paymentType,
    );

    if (paymentType === 'תקבול') {
        return {
            creditAccount: party,
            debitAccount: methodAccountId,
        };
    }

    return {
        debitAccount: party,
        creditAccount: methodAccountId,
    };
};

export default getPaymentAcceptanceLineAccounts;
